/** Admin inbox: list of contact-form submissions with filters + search. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MagnifyingGlass,
  Envelope,
  ArrowRight,
  Circle,
  CaretLeft,
  CaretRight,
} from "@phosphor-icons/react";
import { inboxApi, INBOX_STATUSES, INBOX_MOTIFS } from "../../api/inbox";
import PortalPageHeader from "../../components/portal/PortalPageHeader";

const STATUS_STYLES = {
  new: "bg-blue-50 text-blue-700",
  reviewing: "bg-amber-50 text-amber-700",
  converted: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
};

function StatusBadge({ status }) {
  const { t } = useTranslation();
  return (
    <span
      data-testid={`inbox-badge-${status}`}
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
        STATUS_STYLES[status] || "bg-slate-100 text-slate-500"
      }`}
    >
      {t(`inbox.status.${status}`, { defaultValue: status })}
    </span>
  );
}

const FILTERS = ["all", ...INBOX_STATUSES];

export default function AdminInbox() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("new");
  const [motifFilter, setMotifFilter] = useState("all");
  const [page, setPage] = useState(1);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, cnt] = await Promise.all([
        inboxApi.list({
          status: statusFilter,
          motif: motifFilter,
          page,
          limit,
        }),
        inboxApi.counts(),
      ]);
      setData(list);
      setCounts(cnt);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, motifFilter, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset page when filter changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter, motifFilter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.items;
    return data.items.filter((r) =>
      [r.first_name, r.last_name, r.email, r.message]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(q)),
    );
  }, [data.items, query]);

  const fmtDate = useCallback(
    (iso) =>
      new Date(iso).toLocaleString(i18n.language || "fr", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }),
    [i18n.language],
  );

  return (
    <div data-testid="admin-inbox-page" className="space-y-8">
      <PortalPageHeader
        kickerKey="admin.placeholder.kicker"
        titleKey="inbox.title"
        subtitleKey="inbox.subtitle"
      />

      <div className="space-y-3">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2.5">
          <MagnifyingGlass size={16} weight="regular" className="text-slate-400" />
          <input
            type="text"
            data-testid="admin-inbox-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("inbox.search_placeholder")}
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              data-testid={`inbox-filter-${f}`}
              onClick={() => setStatusFilter(f)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider transition-colors ${
                statusFilter === f
                  ? "bg-slate-900 text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:border-slate-900"
              }`}
            >
              {t(`inbox.filter.${f}`, { defaultValue: f })}
              {typeof counts[f] === "number" && (
                <span
                  className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] ${
                    statusFilter === f
                      ? "bg-white/20 text-white"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {counts[f]}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
            {t("inbox.motif")}
          </span>
          {["all", ...INBOX_MOTIFS].map((m) => (
            <button
              key={m}
              type="button"
              data-testid={`inbox-motif-${m}`}
              onClick={() => setMotifFilter(m)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                motifFilter === m
                  ? "bg-slate-900 text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:border-slate-900"
              }`}
            >
              {m === "all"
                ? t("inbox.filter.all")
                : t(`contact.form.motif_${m}`, { defaultValue: m })}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">{t("common.loading")}</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <Envelope
            size={32}
            weight="light"
            className="mx-auto text-slate-400 mb-3"
          />
          <p className="text-sm text-slate-500">{t("inbox.empty")}</p>
        </div>
      ) : (
        <ul className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {filtered.map((r) => (
            <li key={r.id}>
              <Link
                to={`/admin/inbox/${r.id}`}
                data-testid={`inbox-row-${r.id}`}
                className="group flex items-start gap-4 px-5 py-4 hover:bg-slate-50 transition-colors"
              >
                {r.status === "new" ? (
                  <Circle
                    size={8}
                    weight="fill"
                    className="text-blue-500 mt-2"
                    aria-label={t("inbox.unread")}
                  />
                ) : (
                  <span className="inline-block w-2 mt-2" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p
                      className={`text-sm truncate ${
                        r.status === "new"
                          ? "font-bold text-slate-900"
                          : "font-medium text-slate-800"
                      }`}
                    >
                      {r.first_name} {r.last_name}
                    </p>
                    <StatusBadge status={r.status} />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      ·{" "}
                      {t(`contact.form.motif_${r.motif}`, {
                        defaultValue: r.motif,
                      })}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500 truncate">
                    {r.email} {r.phone ? `· ${r.phone}` : ""}
                  </p>
                  <p className="mt-1 text-sm text-slate-600 line-clamp-1">
                    {r.message}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <p className="text-[11px] text-slate-400 whitespace-nowrap">
                    {fmtDate(r.created_at)}
                  </p>
                  <ArrowRight
                    size={14}
                    weight="bold"
                    className="text-slate-400 group-hover:text-slate-900 group-hover:translate-x-1 transition-all"
                  />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {data.pages > 1 && (
        <div
          data-testid="inbox-pagination"
          className="flex items-center justify-between"
        >
          <p className="text-xs text-slate-500">
            {t("inbox.pagination.range", {
              from: (data.page - 1) * limit + 1,
              to: Math.min(data.page * limit, data.total),
              total: data.total,
            })}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="inbox-prev-page"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={data.page <= 1 || loading}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 disabled:opacity-30 hover:border-slate-900 transition-colors"
              aria-label={t("planning.prev")}
            >
              <CaretLeft size={14} weight="bold" />
            </button>
            <span className="text-xs font-bold text-slate-700 px-2">
              {data.page} / {data.pages}
            </span>
            <button
              type="button"
              data-testid="inbox-next-page"
              onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
              disabled={data.page >= data.pages || loading}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 disabled:opacity-30 hover:border-slate-900 transition-colors"
              aria-label={t("planning.next")}
            >
              <CaretRight size={14} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
